/**
 * Centralised data for the Student Loan Rates & Thresholds page.
 * Update this file each April (thresholds) and September (interest rates)
 * when SLC/GOV.UK publish new figures - keep it in sync with
 * historicalData.js (see that file's header comment for the underlying key
 * semantics); ratesFreshness.test.js checks the two files agree.
 *
 * Sources:
 *  - GOV.UK:  https://www.gov.uk/repaying-your-student-loan
 *  - SLC:     https://www.slc.co.uk/
 */

import figures from "./figures";
import historicalData from "./historicalData";

const currentTaxYear = figures.verified.taxYear;
const { studentLoans: sl } = figures;

const ratesData = {
  taxYear: currentTaxYear,
  lastUpdated: figures.verified.label,
  lastUpdatedDate: figures.verified.on,

  /**
   * Pension tax relief limits.
   *
   * Relief at source tops up what you pay in by basic rate, and it is
   * available even if you pay no income tax at all: up to £3,600 gross a
   * year, which costs £2,880 because the £720 top-up is added for you.
   * Source: GOV.UK "Tax on your private pension contributions".
   */
  pensionRelief: figures.pensionRelief,

  plans: [
    {
      id: "plan1",
      name: "Plan 1",
      who: "Income-contingent loans funded by England or Wales before September 2012, or by Northern Ireland.",
      threshold: sl.plan1.threshold,
      repaymentRate: sl.plan1.repaymentPercent,
      interestRate: "The lower of RPI or the Bank of England base rate + 1%",
      currentInterest: sl.plan1.interestLabel,
      writeOff:
        "25 years after the April you were first due to repay (or age 65 if your first loan payment was before 1 September 2006)",
      writeOffYears: 25,
      writeOffLabel: "25 years or age 65",
    },
    {
      id: "plan2",
      name: "Plan 2",
      who: "Undergraduate or PGCE loans funded by England for courses starting September 2012 to July 2023, or Wales from September 2012 onwards. Also English Higher Education Short Course Loans.",
      threshold: sl.plan2.threshold,
      repaymentRate: sl.plan2.repaymentPercent,
      interestRate: "RPI + 3% while studying, then RPI to RPI + 3% based on income, subject to the current cap",
      currentInterest: sl.plan2.interestLabel,
      writeOff: "30 years after the April you were first due to repay",
      writeOffYears: 30,
    },
    {
      id: "plan4",
      name: "Plan 4",
      who: "Undergraduate or postgraduate loans funded by the Student Awards Agency Scotland.",
      threshold: sl.plan4.threshold,
      repaymentRate: sl.plan4.repaymentPercent,
      interestRate: "The lower of RPI or the Bank of England base rate + 1%",
      currentInterest: sl.plan4.interestLabel,
      writeOff:
        "30 years after the April you were first due to repay (or, if your first loan payment was before 1 August 2007, age 65 or 30 years - whichever comes first)",
      writeOffYears: 30,
      writeOffLabel: "30 years or earlier",
    },
    {
      id: "plan5",
      name: "Plan 5",
      who: "Undergraduate, PGCE or Advanced Learner Loans funded by England for courses starting on or after 1 August 2023.",
      threshold: sl.plan5.threshold,
      repaymentRate: sl.plan5.repaymentPercent,
      interestRate: "RPI only (no income-based margin)",
      currentInterest: sl.plan5.interestLabel,
      writeOff: "40 years after the April you were first due to repay",
      writeOffYears: 40,
    },
    {
      id: "postgrad",
      name: "Postgraduate Loan",
      who: "Master's or Doctoral Loan borrowers (England/Wales).",
      threshold: sl.postgrad.threshold,
      repaymentRate: sl.postgrad.repaymentPercent,
      interestRate:
        "RPI + 3%, subject to the current cap; not based on income",
      currentInterest: sl.postgrad.interestLabel,
      writeOff: "30 years after the April you were first due to repay",
      writeOffYears: 30,
    },
  ],

  /** Thresholds are keyed by the April tax year, shared with the engine. */
  thresholdHistory: Array.from({ length: 6 }, (_, index) => {
    const year = parseInt(currentTaxYear, 10) - index;
    return {
      year: `${year}/${String(year + 1).slice(-2)}`,
      plan1: historicalData.plan1Floor[year],
      plan2: historicalData.currentPaymentFloor[year],
      plan4: historicalData.plan4Floor[year],
      // Plan 5 repayments did not start until April 2026.
      plan5: year >= 2026 ? historicalData.plan5Floor[year] : null,
      postgrad: historicalData.defaultPlanFloor.planPostgrad,
    };
  }),

  /** Interest snapshots are keyed by September, not by the April tax year. */
  interestHistory: Array.from({ length: 6 }, (_, index) => {
    const year = parseInt(currentTaxYear, 10) - index;
    const label = (rate, capped = false) => `${rate}%${capped ? " (max)" : ""}`;
    const maximum = (plan, rawRate) => Math.min(rawRate, historicalData.interestCap[plan][year] ?? rawRate);
    return {
      year: `${year}/${String(year + 1).slice(-2)}`,
      plan1: label(historicalData.plan1Rate[year]),
      plan2: label(maximum("plan2", historicalData.rpi[year] + 3), true),
      plan4: label(historicalData.plan1Rate[year]),
      plan5: year >= 2023 ? label(maximum("plan5", historicalData.rpi[year])) : null,
      postgrad: label(maximum("planPostgrad", historicalData.rpi[year] + 3), true),
    };
  }),

  keyDates: [
    {
      label: "Repayments start",
      detail:
        "The April after you leave your course (or April 4 years after starting, if part-time).",
    },
    {
      label: "Interest starts",
      detail:
        "From the day your first payment is made to your university or bank account.",
    },
    {
      label: "Thresholds update",
      detail:
        "Every April, based on average earnings growth (Plan 2) or RPI (Plans 1 & 4). Plan 5 is frozen at £25,000 until 2027.",
    },
    {
      label: "Rates update",
      detail: "Every September, based on the previous March's RPI figure.",
    },
    {
      label: "Unpaid balance written off",
      detail:
        "25 years (Plan 1), 30 years (Plans 2, 4, Postgrad) or 40 years (Plan 5) after first due to repay.",
    },
  ],
};

export default ratesData;
